import {
  MBMData,
  MBSPricingData,
  PriceResult,
  PricingConfig,
  PricingFactor,
  PricingItem,
  ProfileData
} from 'app/types/pricing'
import {
  equipmentCategory,
  escalatorType,
  getDefaultPricingFactor,
  getMBMFactors,
  getMVinArrayWithChoice,
  getMVTypeValue,
  getProfileChoice,
  getProfileKeyMapping,
  getRangeValue,
  noPriceChoices,
  profileEntry,
  serviceElements,
  specialPriceChoices,
  subfactors,
  typeCheckFactors,
  isValidProfileEntry,
  mandatoryPricingKeys
} from './price-calculation-util'

const isServiceIncluded = (service: string, allowedServices: string[], mandatoryKeys: string[]): boolean =>
  allowedServices.includes(service) || mandatoryKeys.includes(service)

/**
 * Gets price factor value based on choice
 */
function getFactorPrice(
  choice: string | number,
  factorData: PricingItem[],
  dependentPricing = 0,
  isDirectValue = false
): number | string {
  if (!Array.isArray(factorData)) {
    return isDirectValue ? '0' : 0
  }
  const rangeEntry = getRangeValue(Number(choice), factorData)
  if (rangeEntry) {
    return getMVTypeValue(rangeEntry, dependentPricing, isDirectValue)
  }
  const item = factorData.find(factor => factor.choice === choice.toString())
  if (item) {
    return getMVTypeValue(item, dependentPricing, isDirectValue)
  }
  return isDirectValue ? '0' : 0
}

/**
 * Calculate pricing for subfactors
 */
const calculateSubfactors = (
  mbmData: MBSPricingData,
  service: string,
  choice: string | number,
  subFactorsIncluded: string[] = [],
  dependentPricing = 0,
  profile: ProfileData
): PricingFactor[] => {
  const subFactorsData = mbmData[service] || []
  if (!subFactorsData) {
    return []
  }
  const factors = Array.isArray(subFactorsData)
    ? subFactorsData.find(entry => String(entry.choice) === String(choice)) || {}
    : subFactorsData.factors
  return subFactorsIncluded
    .map(factor => {
      if (profile[profileEntry.equipmentType] === escalatorType.autowalk && factor === subfactors.rise) {
        factor = subfactors.length // We are changeing the factor from rise to length for autowalk equipment type only for Escalator
      }
      if (factors[factor] && Array.isArray(factors[factor])) {
        const profileChoice = getProfileChoice(profile, factor)
        return {
          key: factor,
          price: getFactorPrice(profileChoice, factors[factor], dependentPricing),
          value: getFactorPrice(profileChoice, factors[factor], dependentPricing, true),
          choice: profileChoice,
          hasPriceImpact: true
        } as PricingFactor
      }
      return undefined
    })
    .filter((factor): factor is PricingFactor => factor !== undefined)
}

/**
 * Creates a pricing factor from an entry
 */
const createPricingFactorFromEntry = (
  entry: any,
  choice: string | number,
  service: string,
  basePrice: number
): PricingFactor | null => {
  if (typeof entry === typeCheckFactors.object && typeCheckFactors.value in entry && typeCheckFactors.mvType in entry) {
    const price = getMVTypeValue(entry, basePrice) as number
    return {
      key: service,
      price,
      value: getMVTypeValue(entry, basePrice, true),
      choice,
      hasPriceImpact: true
    }
  } else if (Array.isArray(entry) && entry.length >= 2 && typeof entry[1] === typeCheckFactors.object) {
    const value = entry[1]
    if (typeCheckFactors.value in value && typeCheckFactors.mvType in value) {
      return {
        key: service,
        price: getMVTypeValue(value, basePrice) as number,
        value: getMVTypeValue(value, basePrice, true),
        choice,
        hasPriceImpact: true
      }
    }
  }
  return null
}

/**
 * Resolve service value based on profile and configuration
 */
function resolveServiceValue(
  service: string,
  profile: ProfileData,
  basePrice: number,
  mbmData: MBMData
): PricingFactor[] {
  const profileKey = getProfileKeyMapping(service)
  if (
    mbmData[service] &&
    Array.isArray(mbmData[service]) &&
    mbmData[service].some((item: any) => item.range && Array.isArray(item.range))
  ) {
    const profileValue = profile[profileKey] || 0
    const numericValue = typeof profileValue === typeCheckFactors.string ? parseFloat(profileValue) : profileValue
    const matchingEntry = getRangeValue(numericValue, mbmData[service])
    if (matchingEntry) {
      const price = getMVTypeValue(matchingEntry, basePrice) as number
      return [
        {
          key: service,
          price,
          value: getMVTypeValue(matchingEntry, basePrice, true),
          choice: profileValue,
          hasPriceImpact: true
        }
      ]
    }
  }
  const serviceData = mbmData[service]
  if (!serviceData) {
    return [getDefaultPricingFactor(service, profile[profileKey] || 0)]
  }

  if (Array.isArray(serviceData)) {
    if (Array.isArray(profile[profileKey])) {
      const profileValues = profile[profileKey] as Array<string | number>
      const pricingFactors: PricingFactor[] = []
      profileValues.forEach(pv => {
        const entry = serviceData.find(
          (item: any) =>
            (item.choice !== undefined && item.choice === pv.toString()) ||
            (Array.isArray(item) && item.length >= 2 && item[0] === pv)
        )
        if (entry) {
          const factor = createPricingFactorFromEntry(entry, pv, service, basePrice)
          if (factor) {
            pricingFactors.push(factor)
          }
        } else {
          pricingFactors.push(getDefaultPricingFactor(service, pv))
        }
      })
      return pricingFactors
    } else {
      const profileValue = profile[profileKey]?.toString()
      const entry = serviceData.find(
        (item: any) =>
          (item.choice !== undefined && item.choice === profileValue) ||
          (Array.isArray(item) && item.length >= 2 && item[0] === profileValue)
      )
      if (entry) {
        const factor = createPricingFactorFromEntry(entry, profile[profileKey] || 0, service, basePrice)
        if (factor) {
          return [factor]
        }
      }
    }
  }

  return [getDefaultPricingFactor(service, profile[profileKey] || 0)]
}

/**
 * Calculate unrealised productivity pricing factors
 */
function getUnrealisedProductivity(
  service: string,
  choice: string | number,
  basePrice: number,
  mbmData: MBMData
): PricingFactor[] {
  const baseUsageItem = getMBMFactors(mbmData)
  const unrealisedProductivity = baseUsageItem?.baseUsage?.unrealisedProductivity
  if (!unrealisedProductivity) {
    return [getDefaultPricingFactor(service, noPriceChoices.NA)]
  }
  return [
    {
      key: service,
      price: getMVTypeValue(unrealisedProductivity, basePrice) as number,
      value: getMVTypeValue(unrealisedProductivity, basePrice, true) as string,
      choice: noPriceChoices.NA,
      hasPriceImpact: true
    }
  ]
}

/**
 * Calculate choice seven factors for on-demand pricing
 */
const getOnDemandChoiceSeven = (
  choice: string | number,
  serviceData: any,
  allowedChoices?: string[]
): PricingFactor[] => {
  const entrySeven = Array.isArray(serviceData.choices)
    ? serviceData.choices.find((item: any) => item.choice === specialPriceChoices.onDemandSeven)
    : null

  return choice === specialPriceChoices.onDemandSix &&
    (!allowedChoices || allowedChoices.includes(specialPriceChoices.onDemandSeven))
    ? [
        {
          key: serviceElements.KCSM_SR_ONDEMAND,
          price: entrySeven ? (getMVTypeValue(entrySeven, 0) as number) : 0,
          value: entrySeven ? (getMVTypeValue(entrySeven, 0, true) as string) : '0',
          choice: specialPriceChoices.onDemandSeven,
          hasPriceImpact: true
        }
      ]
    : []
}

/**
 * Create package inclusion/exclusion factor
 */
const createPackageInclusionExclusionFactor = (
  choice: string | number,
  inclExclChoiceFactors: PricingFactor[],
  serviceData: any,
  allowedChoices?: string[]
): PricingFactor[] => {
  const onDemandPricingFactor: PricingFactor[] = [
    {
      key: serviceElements.KCSM_SR_ONDEMAND,
      price: 0,
      value: '0',
      choice,
      hasPriceImpact: true
    }
  ]
  const choiceSeven = getOnDemandChoiceSeven(choice, serviceData, allowedChoices)

  const inclusionExclusionFactors = inclExclChoiceFactors.map(factor => ({
    ...factor,
    key:
      choice === specialPriceChoices.onDemandFive
        ? `${serviceElements.KCSM_SR_PKG_INCL} - ${factor.key}`
        : `${serviceElements.KCSM_SR_PKG_EXCL} - ${factor.key}`
  }))
  return [...onDemandPricingFactor, ...inclusionExclusionFactors, ...choiceSeven]
}
/**
 * Calculate on-demand service pricing for both elevators and escalators, with special handling for escalator subfactors
 * Array of pricing factors containing:
 *   - Default pricing factor if service data is missing
 *   - Package inclusion/exclusion factors with their subfactors
 *   - On-demand pricing factors based on selected choice
 *   - Service-specific pricing details (price, value, choice, and price impact flag)
 */
function getOnDemandService(
  profile: ProfileData,
  service: string,
  choice: string | number,
  basePrice: number,
  mbmData: MBMData,
  priceEnabledServices: string[],
  allowedChoices?: string[],
  isEscalator = false
): PricingFactor[] {
  if (!mbmData[service]) {
    return [getDefaultPricingFactor(service, choice)]
  }

  const onDemandPkgConfig = {
    [specialPriceChoices.onDemandFive]: serviceElements.KCSM_SR_PKG_INCL,
    [specialPriceChoices.onDemandSix]: serviceElements.KCSM_SR_PKG_EXCL
  }

  // Determine subfactor based on equipment type for escalators
  const subfactor = isEscalator
    ? profile[profileEntry.equipmentType] === escalatorType.autowalk
      ? subfactors.length
      : subfactors.rise
    : subfactors.numberOfDoors

  const srOnDemandFactors =
    choice !== specialPriceChoices.onDemandFive
      ? calculateSubfactors(mbmData, service, choice, [subfactor], undefined, profile).map(factor => ({
          ...factor,
          key: `${service} - ${subfactor}`
        }))
      : []

  const serviceElement = onDemandPkgConfig[choice as keyof typeof onDemandPkgConfig]
  if (serviceElement && !isServiceIncluded(serviceElement, priceEnabledServices, mandatoryPricingKeys)) {
    const choiceSevenFactors =
      choice === specialPriceChoices.onDemandSix
        ? [getOnDemandChoiceSeven(choice, mbmData[service], allowedChoices), ...srOnDemandFactors].flat()
        : []

    return [
      getDefaultPricingFactor(service + (serviceElement ? ` - (${serviceElement})` : ''), choice),
      ...choiceSevenFactors
    ]
  }

  if (serviceElement && profile[serviceElement] !== null) {
    const newValues = profile[serviceElement].map((item: any) => {
      const subTotal = calculateSubfactors(mbmData, serviceElement, item, [subfactor], undefined, profile)
      return subTotal.map(factor => ({
        ...factor,
        choice: item
      }))
    })

    return [
      ...createPackageInclusionExclusionFactor(choice, newValues.flat(), mbmData[service], allowedChoices),
      ...srOnDemandFactors
    ]
  }

  const serviceData = mbmData[service]
  const entry = Array.isArray(serviceData.choices)
    ? serviceData.choices.find((item: any) => item.choice === choice.toString())
    : null

  if (!entry) {
    return [getDefaultPricingFactor(service, choice)]
  }

  return [
    {
      key: service,
      price: getMVTypeValue(entry, basePrice) as number,
      value: getMVTypeValue(entry, basePrice, true) as string,
      choice,
      hasPriceImpact: true
    },
    ...srOnDemandFactors
  ]
}

/**
 * Calculate care inspection service pricing
 */
function getCareInspectionService(
  profile: ProfileData,
  service: string,
  choice: string | number,
  basePrice: number,
  mbmData: MBMData
): PricingFactor[] {
  if (!mbmData[service]) {
    return [getDefaultPricingFactor(service, choice)]
  }
  const serviceData = mbmData[service]
  const entry = Array.isArray(serviceData.choices)
    ? serviceData.choices.find((item: any) => item.choice === choice.toString())
    : null
  const pricingFactors: PricingFactor[] = []
  if (choice === specialPriceChoices.onCareInspectionThree) {
    pricingFactors.push(
      ...(calculateSubfactors(
        mbmData,
        serviceElements.KCSM_CARE_INSPECTION,
        profile[serviceElements.KCSM_CARE_INSPECTION],
        [subfactors.numberOfDoors],
        undefined,
        profile
      ) || [])
    )
  }
  if (!entry) {
    return [getDefaultPricingFactor(service, choice)]
  }
  pricingFactors.push({
    key: service,
    price: getMVTypeValue(entry, basePrice) as number,
    value: getMVTypeValue(entry, basePrice, true) as string,
    choice,
    hasPriceImpact: true
  })
  return pricingFactors
}

/**
 * Calculate travel time pricing factors
 */
const getTravelTime = (
  key: string,
  choice: string | number,
  mbmData: MBMData,
  priceEnabledServices: string[]
): PricingFactor[] => {
  const selectedRange = getMVinArrayWithChoice(mbmData[key] || [], choice.toString()) || { value: 0, mv_type: '' }
  const baseUsageItem = getMBMFactors(mbmData)
  const baseUsage = baseUsageItem?.baseUsage || {}
  const {
    hourlyRate: { value: hrlyRate = 0 },
    averageTravelTime: { value: avgTravelTime = 0 }
  } = baseUsage
  const isKcsmDistancePriceEnabled = isServiceIncluded(
    serviceElements.KCSM_DISTANCE_RANGE,
    priceEnabledServices,
    mandatoryPricingKeys
  )
  const distanceRange = isKcsmDistancePriceEnabled ? 1 + Number(getMVTypeValue(selectedRange, 1)) : 1
  const travelTimePrice = avgTravelTime * distanceRange * hrlyRate || 0

  return [
    {
      key: subfactors.hourlyRate,
      price: hrlyRate,
      value: hrlyRate.toString(),
      choice: 0
    },
    {
      key: subfactors.averageTravelTime,
      price: avgTravelTime,
      value: avgTravelTime.toString(),
      choice: 0
    },
    {
      key: serviceElements.KCSM_DISTANCE_RANGE,
      price: distanceRange,
      value: selectedRange.mv_type === typeCheckFactors.percentage ? `${selectedRange.value}%` : noPriceChoices.ZERO,
      choice
    },
    {
      key: subfactors.travelTime,
      price: travelTimePrice,
      value: travelTimePrice.toString(),
      choice: noPriceChoices.NA,
      hasPriceImpact: true
    }
  ]
}

/**
 * Main function to calculate prices based on profile and configuration
 */
export const calculatePrices = (
  profile: ProfileData,
  mbmData: MBMData,
  pricingConfigList: PricingConfig[],
  pricingOptions?: Record<string, any>,
  onDemandAllowedChoices?: string[]
): PriceResult[] => {
  const results: PriceResult[] = []
  const priceMap: Record<string, number> = {}
  pricingConfigList.forEach(priceConfig => {
    const {
      title,
      priceKey,
      isDependent,
      dependentPricing,
      servicesIncluded = [],
      calculatedPriceKeys = [],
      subFactorsIncluded = []
    } = priceConfig

    const factorDetails: PricingFactor[] = []
    const dependentBasePrice = isDependent && dependentPricing ? priceMap[dependentPricing] || 0 : 0

    const equipmentType = profile[profileEntry.equipmentCategory]
    const equipmentKey = equipmentType === equipmentCategory.elevator ? 'elevator' : 'escalator'
    const priceEnabledServices = pricingOptions?.[equipmentKey] || []

    const factors = servicesIncluded.map(service => {
      if (!isServiceIncluded(service, priceEnabledServices, mandatoryPricingKeys) && subFactorsIncluded.length === 0) {
        return getDefaultPricingFactor(service, profile[service] || 0)
      }

      // Handle special case for KCSM_CO
      if (service === serviceElements.KCSM_CO) {
        const isCoIncluded = isServiceIncluded(service, priceEnabledServices, mandatoryPricingKeys)
        const isNbrCoYearIncluded = isServiceIncluded(
          serviceElements.KCSM_NBR_CO_YEAR,
          priceEnabledServices,
          mandatoryPricingKeys
        )
        if (!isCoIncluded) {
          if (!isNbrCoYearIncluded) {
            return getDefaultPricingFactor(
              !isCoIncluded ? serviceElements.KCSM_CO : serviceElements.KCSM_NBR_CO_YEAR,
              profile[service] || 0
            )
          }
          if (!isValidProfileEntry(profile[serviceElements.KCSM_NBR_CO_YEAR])) {
            return getDefaultPricingFactor(service, profile[service] || 0)
          }
          service = serviceElements.KCSM_NBR_CO_YEAR
        } else if (!isValidProfileEntry(profile[service])) {
          service = serviceElements.KCSM_NBR_CO_YEAR
        }
      }
      const serviceSubFactors =
        service === serviceElements.KCSM_NBR_CO_YEAR && equipmentType === equipmentCategory.elevator
          ? subFactorsIncluded.filter(factor => factor !== subfactors.equipmentAge)
          : subFactorsIncluded
      // Skip validation for KCSM_CARE_MBM and unrealisedProductivity, validate others
      const skipValidation = service === serviceElements.KCSM_CARE_MBM || service === subfactors.unrealisedProductivity
      const profileKey = getProfileKeyMapping(service)
      if (!skipValidation && !isValidProfileEntry(profile[profileKey])) {
        return getDefaultPricingFactor(service, profile[service] || 0)
      }
      switch (service) {
        case serviceElements.KCSM_DISTANCE_RANGE:
          return getTravelTime(service, profile[service] || 0, mbmData, priceEnabledServices)
        case subfactors.unrealisedProductivity:
          return profile.isKoneCareTier
            ? getUnrealisedProductivity(service, profile[serviceElements.KCSM_CARE_MBM], dependentBasePrice, mbmData)
            : [getDefaultPricingFactor(service, noPriceChoices.NA)]
        case serviceElements.KCSM_SR_ONDEMAND:
          return serviceSubFactors.length === 0
            ? getOnDemandService(
                profile,
                service,
                profile[serviceElements.KCSM_SR_ONDEMAND],
                dependentBasePrice,
                mbmData,
                priceEnabledServices,
                onDemandAllowedChoices,
                equipmentType === equipmentCategory.escalator
              )
            : calculateSubfactors(
                mbmData,
                service,
                profile[service] || 0,
                serviceSubFactors,
                dependentBasePrice,
                profile
              )

        case serviceElements.KCSM_CARE_INSPECTION:
          return profile[profileEntry.equipmentCategory] === equipmentCategory.elevator
            ? getCareInspectionService(
                profile,
                service,
                profile[serviceElements.KCSM_CARE_INSPECTION],
                dependentBasePrice,
                mbmData
              )
            : resolveServiceValue(service, profile, dependentBasePrice, mbmData)
        default:
          return serviceSubFactors.length > 0
            ? calculateSubfactors(
                mbmData,
                service,
                profile[service] || 0,
                serviceSubFactors,
                dependentBasePrice,
                profile
              )
            : resolveServiceValue(service, profile, dependentBasePrice, mbmData)
      }
    })
    factorDetails.push(...factors.flat().filter(factor => factor != null))
    calculatedPriceKeys.forEach(pk => {
      if (priceMap[pk] !== undefined) {
        factorDetails.push({
          key: pk,
          price: priceMap[pk],
          value: noPriceChoices.NA,
          choice: noPriceChoices.NA,
          hasPriceImpact: true
        })
      }
    })
    const totalPrice = factorDetails
      .filter(factor => factor.hasPriceImpact)
      .reduce((sum, factor) => sum + factor.price, 0)

    priceMap[priceKey] = totalPrice

    results.push({
      title: title || '',
      priceKey,
      price: totalPrice,
      basePrice: isDependent ? dependentBasePrice : undefined,
      factorDetails,
      basePriceKey: dependentPricing
    })
  })
  results.push({
    title:
      profile.equipmentCategory === escalatorType.escalator
        ? `Escalator Price - ${profile.equipmentName}`
        : `Elevator Price - ${profile.equipmentName}`,
    priceKey: profile.equipmentCategory,
    price: 0,
    factorDetails: []
  })
  return results
}
